const originalMuted = new WeakMap<HTMLMediaElement, boolean>();
const originalIframeSrc = new WeakMap<HTMLIFrameElement, string>();
let observer: MutationObserver | null = null;
let isEnabled = false;

function muteEl(el: HTMLMediaElement) {
    if (!originalMuted.has(el)) {
        originalMuted.set(el, el.muted);
    }
    el.muted = true;
}

function unmuteEl(el: HTMLMediaElement) {
    if (originalMuted.has(el)) {
        el.muted = originalMuted.get(el)!;
        originalMuted.delete(el);
    }
}

function isYouTubeEmbed(src: string): boolean {
    return /^https?:\/\/(www\.)?(youtube|youtube-nocookie)\.com\/embed\//i.test(src);
}

function isVimeoEmbed(src: string): boolean {
    return /^https?:\/\/player\.vimeo\.com\/video\//i.test(src);
}

// Try the YouTube/Vimeo postMessage Player API. Only works when the iframe was
// loaded with enablejsapi=1 (YouTube) or api=1 (Vimeo). Best-effort — failures
// are silent. The URL-parameter fallback below covers the rest.
function postMessageMute(iframe: HTMLIFrameElement) {
    const src = iframe.src || '';
    try {
        if (isYouTubeEmbed(src)) {
            iframe.contentWindow?.postMessage(
                JSON.stringify({ event: 'command', func: 'mute', args: [] }),
                '*'
            );
        } else if (isVimeoEmbed(src)) {
            iframe.contentWindow?.postMessage(
                JSON.stringify({ method: 'setVolume', value: 0 }),
                '*'
            );
        }
    } catch {
        // ignore — postMessage on cross-origin frames can throw in some browsers
    }
}

function postMessageUnmute(iframe: HTMLIFrameElement) {
    const src = iframe.src || '';
    try {
        if (isYouTubeEmbed(src)) {
            iframe.contentWindow?.postMessage(
                JSON.stringify({ event: 'command', func: 'unMute', args: [] }),
                '*'
            );
        } else if (isVimeoEmbed(src)) {
            iframe.contentWindow?.postMessage(
                JSON.stringify({ method: 'setVolume', value: 1 }),
                '*'
            );
        }
    } catch {
        // ignore
    }
}

function muteIframe(iframe: HTMLIFrameElement) {
    const src = iframe.src;
    if (!src) return;

    // First try postMessage (no reload). Works only if enablejsapi=1 was preset.
    postMessageMute(iframe);

    let url: URL;
    try {
        url = new URL(src);
    } catch {
        return;
    }

    let needsReload = false;
    if (isYouTubeEmbed(src)) {
        if (url.searchParams.get('mute') !== '1') {
            url.searchParams.set('mute', '1');
            needsReload = true;
        }
        // Set enablejsapi so future toggles can use postMessage without reloading.
        if (url.searchParams.get('enablejsapi') !== '1') {
            url.searchParams.set('enablejsapi', '1');
            needsReload = true;
        }
    } else if (isVimeoEmbed(src)) {
        if (url.searchParams.get('muted') !== '1') {
            url.searchParams.set('muted', '1');
            needsReload = true;
        }
    } else {
        return; // unsupported iframe — nothing we can do safely
    }

    if (needsReload) {
        if (!originalIframeSrc.has(iframe)) {
            originalIframeSrc.set(iframe, src);
        }
        iframe.src = url.toString();
    }
}

function unmuteIframe(iframe: HTMLIFrameElement) {
    if (originalIframeSrc.has(iframe)) {
        iframe.src = originalIframeSrc.get(iframe)!;
        originalIframeSrc.delete(iframe);
        return;
    }
    // No URL was rewritten (we used postMessage only) — undo via postMessage.
    postMessageUnmute(iframe);
}

function muteAllExisting() {
    document.querySelectorAll<HTMLMediaElement>('audio, video').forEach(muteEl);
    document.querySelectorAll<HTMLIFrameElement>('iframe').forEach(muteIframe);
}

function unmuteAllTracked() {
    document.querySelectorAll<HTMLMediaElement>('audio, video').forEach(unmuteEl);
    document.querySelectorAll<HTMLIFrameElement>('iframe').forEach(unmuteIframe);
}

function startObserver() {
    if (observer) return;
    observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
            m.addedNodes.forEach((node) => {
                if (!(node instanceof HTMLElement)) return;
                if (node instanceof HTMLMediaElement) {
                    muteEl(node);
                } else if (node instanceof HTMLIFrameElement) {
                    muteIframe(node);
                } else {
                    node.querySelectorAll<HTMLMediaElement>('audio, video').forEach(muteEl);
                    node.querySelectorAll<HTMLIFrameElement>('iframe').forEach(muteIframe);
                }
            });
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });
}

function stopObserver() {
    if (observer) {
        observer.disconnect();
        observer = null;
    }
}

export default function muteSounds(enable = false) {
    if (enable && !isEnabled) {
        muteAllExisting();
        startObserver();
        isEnabled = true;
    } else if (!enable && isEnabled) {
        stopObserver();
        unmuteAllTracked();
        isEnabled = false;
    }
}
